import { Youtube, Smartphone } from "lucide-react";
import { Link } from "react-router-dom";

const XIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const TikTokIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12.525.02c1.8-.1 3.74.521 4.61 2.14.45-.06.9-.12 1.35-.18a6.84 6.84 0 0 1-.48 3.3 6.59 6.59 0 0 0 2.8-1.78 13.3 13.3 0 0 1-1.7 2.62c-.01.31-.02.63-.03.94 0 3.86-2.01 6.64-5.44 6.8-1.21.05-2.38-.4-3.23-1.13-.15-.14-.29-.28-.42-.44-.72-.85-1.1-1.92-1.06-3.02.05-1.15.52-2.24 1.3-3.04.92-.93 2.17-1.39 3.43-1.24 1.1.13 2.09.73 2.71 1.62.11-1.35.17-2.7.17-4.05 0-.27-.01-.55-.02-.82-2.15 0-4.06-1.06-5.22-2.68a9.6 9.6 0 0 1-1.22 4.66 8.83 8.83 0 0 0-.89-3.58c-.31 1.6-.48 3.25-.48 4.95 0 5.52 4.48 10 10 10s10-4.48 10-10S18.545.02 12.525.02z" />
  </svg>
);

type FooterLink = { label: string; to?: string; href?: string };

const cols: { title: string; links: FooterLink[] }[] = [
  {
    title: "Product",
    links: [
      { label: "Features", to: "/features" },
      { label: "Pricing", to: "/pricing" },
      { label: "Integrations", to: "/integrations" },
      { label: "Industries", to: "/industries" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About", to: "/about" },
      { label: "Customers", to: "/customers" },
      { label: "Contact", href: "https://help.freecrm.com/hc/en-us/requests/new" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "Help center", href: "https://help.freecrm.com/hc/en-us" },
      { label: "API docs", to: "/api-docs" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Privacy", to: "/privacy" },
      { label: "Terms", to: "/terms" },
    ],
  },
];

const Footer = () => {
  return (
    <footer className="border-t border-border bg-card">
      <div className="container py-16">
        <div className="grid gap-10 md:grid-cols-5">
          <div className="md:col-span-1">
            <Link to="/" className="flex items-center gap-2 font-display text-xl font-bold text-secondary">
              <img src="/favicon.png" alt="FreeCRM logo" className="h-8 w-8 object-contain" />
              FreeCRM
            </Link>
            <p className="mt-4 text-sm text-muted-foreground">
              The free CRM that grows with your business.
            </p>
            <div className="mt-4 flex flex-col gap-2">
              <a
                href="https://apps.apple.com/us/app/freecrm-com/id6757925489"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-primary"
              >
                <Smartphone className="h-4 w-4" />
                iOS App
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.freecrm"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-primary"
              >
                <Smartphone className="h-4 w-4" />
                Android App
              </a>
              <a
                href="https://youtube.com/@getfreecrm?si=ObfGWQD5IlDNhbB8"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-primary"
              >
                <Youtube className="h-4 w-4" />
                YouTube
              </a>
              <a
                href="https://x.com/freecrm"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-primary"
              >
                <XIcon className="h-4 w-4" />
                X
              </a>
              <a
                href="https://www.tiktok.com/@freecrm"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-primary"
              >
                <TikTokIcon className="h-4 w-4" />
                TikTok
              </a>
            </div>
          </div>
          {cols.map((c) => (
            <div key={c.title}>
              <h4 className="font-display font-semibold text-secondary">{c.title}</h4>
              <ul className="mt-4 space-y-2.5">
                {c.links.map((l) => (
                  <li key={l.label}>
                    {l.to ? (
                      <Link
                        to={l.to}
                        className="text-sm text-muted-foreground transition-colors hover:text-primary"
                      >
                        {l.label}
                      </Link>
                    ) : (
                      <a
                        href={l.href}
                        className="text-sm text-muted-foreground transition-colors hover:text-primary"
                      >
                        {l.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-12 flex flex-col items-center justify-between gap-3 border-t border-border pt-8 text-sm text-muted-foreground md:flex-row">
          <span>© {new Date().getFullYear()} FreeCRM. All rights reserved.</span>
          <span>Built for teams who deserve better than HubSpot.</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
